import { Block } from '@/components/base/Block'
import { Text3, Text4 } from '@/components/base/Text'
import { useApp } from '@/hooks/useApp'
import { type BorderRadiusProps } from '@/styles/border_radius'
import { cursor } from '@/styles/cursor'
import { align } from '@/styles/display'
import { fontWeight } from '@/styles/font'
import spaceStyle, { type SpacingProps } from '@/styles/space'
import { whiteSpace } from '@/styles/white_space'
import { useState, useRef, useEffect } from 'react'

interface AdvisorCommentContentProps {
  isShow?: boolean
  text: string
  padding?: SpacingProps | undefined
  borderRadius?: BorderRadiusProps | undefined
}

const MAX_LINE_SHOW = 3

const AdvisorCommentContent = ({
  isShow,
  text,
  padding,
  borderRadius
}: AdvisorCommentContentProps): JSX.Element => {
  const app = useApp()

  const textRef = useRef<HTMLDivElement>(null)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isShowFullText, setIsShowFullText] = useState(false)
  const [truncatedText, setTruncatedText] = useState(text)

  const maxHeightInEm = 0.86 * MAX_LINE_SHOW
  let visibleTextHeight = maxHeightInEm

  const calculateVisibleTextHeight = (): void => {
    if (text.length > 100) {
      setTruncatedText(text.substring(0, 100))
      setIsCollapsed(true)
    } else {
      setTruncatedText(text)
      setIsCollapsed(false)
    }

    if (textRef.current != null) {
      const element = textRef.current
      const fontSize = parseFloat(window.getComputedStyle(element).fontSize)
      const heightInPx = element.scrollHeight
      const heightInEm = heightInPx / fontSize

      if (heightInEm <= maxHeightInEm) {
        visibleTextHeight = heightInEm
      } else {
        visibleTextHeight = maxHeightInEm
        setIsCollapsed(true)
      }
    }
  }

  useEffect(() => {
    calculateVisibleTextHeight()
    window.addEventListener('resize', calculateVisibleTextHeight)
    return () => {
      window.removeEventListener('resize', calculateVisibleTextHeight)
    }
  }, [text, isShowFullText])

  return (
    <>
      {(isShow ?? false) && (
        <Block
          textAlign={align.LEFT}
          backgroundColor={app.theme.yellowLightColor}
          padding={padding}
          borderRadius={borderRadius}
        >
          <Text3
            margin={{ vertical: spaceStyle.NONE }}
            fontWeight={fontWeight.Bold}
          >
            担当アドバイザーからのコメント
          </Text3>
          <Block
            textAlign={align.LEFT}
            style={{
              overflow: 'hidden',
              textOverflow: 'clip',
              maxHeight: isShowFullText ? 'none' : `${visibleTextHeight}em`,
              lineHeight: 1.1
            }}
          >
            <div ref={textRef}>
              <Text4
                textColor={app.theme.mainTextColor}
                margin={{ horizontal: spaceStyle.NONE }}
                whiteSpace={whiteSpace.PRE_LINE}
              >
                {isShowFullText || visibleTextHeight < maxHeightInEm
                  ? text
                  : truncatedText}
              </Text4>
            </div>
          </Block>

          {isCollapsed ? (
            <Block
              width={'100%'}
              textAlign={align.LEFT}
              margin={{ top: isShowFullText ? spaceStyle.M : spaceStyle.NONE }}
            >
              <Text3
                textColor={app.theme.mainTextColor}
                fontWeight={fontWeight.Bold}
                margin={{ all: spaceStyle.NONE }}
                underline
                cursor={cursor.POINTER}
                onClick={() => {
                  setIsShowFullText(!isShowFullText)
                }}
              >
                {isShowFullText ? '閉じる' : '…続きを読む'}
              </Text3>
            </Block>
          ) : (
            <></>
          )}
        </Block>
      )}
    </>
  )
}

export default AdvisorCommentContent
